import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { getCMSContent } from '@/lib/api';

const DAYS = ['lundi','mardi','mercredi','jeudi','vendredi','samedi','dimanche'];
const DAY_SHORT = {
  lundi:'Lun.', mardi:'Mar.', mercredi:'Mer.',
  jeudi:'Jeu.', vendredi:'Ven.', samedi:'Sam.', dimanche:'Dim.'
};

/** Regroupe les jours qui ont les mêmes horaires et formate l'affichage. */
function groupDaysByHours(cms) {
  // Construire map hours → [days] en préservant l'ordre d'apparition
  const hoursMap = {};
  const order = [];
  DAYS.forEach(day => {
    const h = (cms[`hours_${day}`] || '').trim();
    if (!h) return;
    if (!hoursMap[h]) { hoursMap[h] = []; order.push(h); }
    hoursMap[h].push(day);
  });

  return order.map(hours => {
    const days = hoursMap[hours];
    let label;
    if (days.length === 1) {
      label = DAY_SHORT[days[0]];
    } else {
      const indices = days.map(d => DAYS.indexOf(d));
      const isConsec = indices.every((idx, i) => i === 0 || idx === indices[i - 1] + 1);
      label = isConsec
        ? `${DAY_SHORT[days[0]]} - ${DAY_SHORT[days[days.length - 1]]}`
        : days.map(d => DAY_SHORT[d]).join(', ');
    }
    return { label, hours };
  });
}

export const Footer = () => {
  const [cms, setCms] = useState({});

  useEffect(() => {
    getCMSContent()
      .then(res => {
        // /api/cms retourne directement un objet {key: value}
        setCms(res.data || {});
      })
      .catch(() => {});
  }, []);

  const phone   = cms['phone_number'] || '';
  const email   = cms['contact_email'] || '';
  const address = cms['address'] || '26 rue Marceau, 37000 Tours, France';
  const hoursGroups = groupDaysByHours(cms);

  return (
    <footer className="bg-stone-100 border-t border-border">
      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <Link to="/" className="inline-block mb-4">
              <span className="font-heading text-3xl font-semibold text-foreground">
                Villa Marceau
              </span>
            </Link>
            <p className="text-muted-foreground leading-relaxed max-w-md">
              Votre concept store dédié à l'art du tapis et de la décoration intérieure.
              Des pièces uniques, artisanales, pour sublimer votre intérieur.
            </p>
          </div>

          {/* Navigation rapide */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-4">Navigation</h4>
            <ul className="space-y-2 text-muted-foreground">
              <li><Link to="/notre-histoire"      className="hover:text-primary transition-colors">Notre histoire</Link></li>
              <li><Link to="/commander-en-ligne"   className="hover:text-primary transition-colors">Commander en ligne</Link></li>
              <li><Link to="/ou-nous-trouver"      className="hover:text-primary transition-colors">Où nous trouver</Link></li>
              <li><Link to="/blog"                 className="hover:text-primary transition-colors">Blog</Link></li>
              <li><Link to="/suivi-commande"       className="hover:text-primary transition-colors">Suivre ma commande</Link></li>
            </ul>
          </div>

          {/* Contact — dynamique via CMS */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-muted-foreground">
                <MapPin className="w-4 h-4 mt-1 flex-shrink-0 text-primary" />
                <span>
                  {address.split(',').map((part, i) => (
                    <span key={i}>{i > 0 && <br />}{part.trim()}</span>
                  ))}
                </span>
              </li>
              {phone && (
                <li className="flex items-center gap-3 text-muted-foreground">
                  <Phone className="w-4 h-4 flex-shrink-0 text-primary" />
                  <a href={`tel:${phone}`} className="hover:text-primary transition-colors">{phone}</a>
                </li>
              )}
              {email && (
                <li className="flex items-center gap-3 text-muted-foreground">
                  <Mail className="w-4 h-4 flex-shrink-0 text-primary" />
                  <a href={`mailto:${email}`} className="hover:text-primary transition-colors">{email}</a>
                </li>
              )}
            </ul>
          </div>

          {/* Horaires — dynamique + regroupement */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-4">Horaires</h4>
            {hoursGroups.length > 0 ? (
              <ul className="space-y-2">
                {hoursGroups.map(({ label, hours }) => {
                  const isClosed = hours.toLowerCase() === 'fermé' || hours.toLowerCase() === 'ferme';
                  return (
                    <li key={label} className="flex items-start gap-2 text-sm">
                      <Clock className="w-4 h-4 flex-shrink-0 text-primary mt-0.5" />
                      <span className="text-muted-foreground">
                        <span className="font-medium text-foreground">{label}</span>
                        {' '}
                        <span className={isClosed ? 'text-red-500' : ''}>{hours}</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground italic">Horaires à renseigner</p>
            )}
          </div>
        </div>
      </div>

      {/* Giant brand name */}
      <div className="overflow-hidden py-8 border-t border-border/50">
        <h2 className="font-heading text-[8rem] md:text-[12rem] lg:text-[16rem] font-bold text-stone-200 text-center whitespace-nowrap leading-none select-none">
          Villa Marceau
        </h2>
      </div>

      {/* Copyright */}
      <div className="bg-stone-200/50 py-4">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p>© 2025 Villa Marceau. Tous droits réservés.</p>
          <div className="flex items-center gap-6">
            <Link to="/admin" className="hover:text-foreground transition-colors" data-testid="admin-link">
              Administration
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
